
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { GeneratedCover } from '../components/ui/GeneratedCover';
import { Button } from '../components/ui/Button';
import { ChevronLeft, Upload, PenTool, BookOpen, CheckCircle, AlertCircle, Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { createBook, getMyBook, updateBook } from '../services/api';
import { GENRES } from '../services/genres';

export const AddBook: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bookId = searchParams.get('bookId');
  const { surfaceClass, bgClass } = useTheme();

  const [formData, setFormData] = useState({
    title: '',
    author: user?.name || '',
    genre: 'Fantasy',
    description: '',
    language: 'English',
  });

  const [chapters, setChapters] = useState<{title: string, content: string}[]>([
    { title: 'Chapter 1', content: '' }
  ]);

  const [expandedChapterIndex, setExpandedChapterIndex] = useState<number | null>(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!bookId) return;
      setLoading(true);
      setError(null);
      try {
        const data = await getMyBook(bookId);
        setFormData({
          title: data.title || '',
          author: data.author || '',
          genre: data.genre || 'Fantasy',
          description: data.description || '',
          language: data.language || 'English',
        });
        setChapters(
          (data.chapters || []).map((c: any, idx: number) => ({
            title: c.title || `Chapter ${idx + 1}`,
            content: c.content_html || '',
            isFree: !!c.is_free,
          }))
        );
        setExpandedChapterIndex(0);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [bookId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleChapterChange = (index: number, field: 'title' | 'content', value: string) => {
    const newChapters = [...chapters];
    newChapters[index][field] = value;
    setChapters(newChapters);
  };

  const addChapter = () => {
    const newIndex = chapters.length;
    setChapters([...chapters, { title: `Chapter ${newIndex + 1}`, content: '' }]);
    setExpandedChapterIndex(newIndex);
  };

  const removeChapter = (index: number) => {
    if (chapters.length === 1) {
        alert("A book must have at least one chapter.");
        return;
    }
    const newChapters = chapters.filter((_, i) => i !== index);
    setChapters(newChapters);
    setExpandedChapterIndex(newChapters.length - 1);
  };

  const toggleChapter = (index: number) => {
    setExpandedChapterIndex(expandedChapterIndex === index ? null : index);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    // Validate
    if (chapters.some(c => !c.title || !c.content)) {
        alert("Please ensure all chapters have a title and content.");
        setIsSubmitting(false);
        return;
    }

    try {
      const payload = {
        ...formData,
        chapters: chapters.map((c, idx) => ({
          title: c.title,
          content: c.content,
          isFree: c.isFree ?? idx < 2,
        })),
      };

      if (bookId) {
        await updateBook(bookId, payload);
      } else {
        await createBook(payload);
      }
      navigate(`/profile`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) {
    return (
        <div className="flex flex-col items-center justify-center min-h-[50vh] p-6 text-center">
            <AlertCircle size={48} className="text-red-500 mb-4" />
            <h2 className="text-xl font-bold mb-2">Access Restricted</h2>
            <p className="opacity-60">You must be logged in to publish books.</p>
            <Button className="mt-4" onClick={() => navigate('/library')}>Go to Login</Button>
        </div>
    )
  }

  return (
    <div className="pb-24 px-6 pt-6 min-h-screen animate-in slide-in-from-bottom-4 duration-500">
      
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-colors">
          <ChevronLeft size={24} />
        </button>
        <h1 className="font-serif text-2xl font-bold">{bookId ? 'Edit Case File' : 'Publish New Case File'}</h1>
      </div>

      {loading && <p className="text-sm opacity-60">Loading...</p>}

      <div className="grid md:grid-cols-2 gap-8 items-start">
        {error && <div className="md:col-span-2 text-red-500 text-sm">{error}</div>}
        
        {/* Left Col: Live Preview (Sticky on Desktop) */}
        <div className="md:sticky md:top-24 flex flex-col items-center">
            <div className="mb-4 text-xs font-bold uppercase tracking-wider opacity-50">Live Cover Preview</div>
            <div className="relative group transition-transform duration-500 hover:scale-105">
                <GeneratedCover 
                    title={formData.title || "Untitled Case"} 
                    author={formData.author || user?.name || "Unknown Author"} 
                    genre={formData.genre} 
                    size="xl"
                    className="shadow-2xl" 
                />
                <div className="absolute -right-4 -bottom-4 bg-white dark:bg-zinc-800 text-brand-blue p-3 rounded-full shadow-lg border border-black/5 dark:border-white/5">
                    <PenTool size={20} />
                </div>
            </div>
            <p className="mt-6 text-sm text-center opacity-60 max-w-xs leading-relaxed">
                The cover is auto-generated from your title and genre. You handle the content; we handle the wrapper.
            </p>
        </div>

        {/* Right Col: Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* Metadata Section */}
            <div className={`p-6 rounded-2xl border shadow-sm ${surfaceClass}`}>
                <h2 className="font-bold text-lg mb-1">Case Details</h2>
                <p className="text-sm opacity-60 mb-4">Give readers enough to open the file. You can edit anytime.</p>
                
                <div className="space-y-4">
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider opacity-50 mb-1.5">Title</label>
                        <p className="text-xs opacity-60 mb-1">Give the case a name readers will remember.</p>
                        <input 
                            type="text" 
                            name="title"
                            required
                            placeholder="Untitled Case"
                            className="w-full bg-black/5 dark:bg-white/5 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-brand-blue transition-all font-serif text-lg placeholder:opacity-30"
                            value={formData.title}
                            onChange={handleInputChange}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider opacity-50 mb-1.5">Author</label>
                            <p className="text-xs opacity-60 mb-1">How you appear in the lab.</p>
                            <input 
                                type="text" 
                                name="author"
                                required
                                className="w-full bg-black/5 dark:bg-white/5 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-brand-blue transition-all"
                                value={formData.author}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider opacity-50 mb-1.5">Genre</label>
                            <p className="text-xs opacity-60 mb-1">Pick the closest genre. Stories often spill over the edges.</p>
                            <div className="relative">
                                <select 
                                    name="genre"
                                    className="w-full bg-black/5 dark:bg-white/5 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-brand-blue transition-all appearance-none"
                                    value={formData.genre}
                                    onChange={handleInputChange}
                                >
                                    {GENRES.map(g => <option key={g.slug} value={g.name}>{g.name}</option>)}
                                </select>
                                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none opacity-50">▼</div>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider opacity-50 mb-1.5">Description</label>
                        <p className="text-xs opacity-60 mb-1">What is this about, and what might someone notice after reading?</p>
                        <textarea 
                            name="description"
                            required
                            rows={4}
                            placeholder="Write a short summary..."
                            className="w-full bg-black/5 dark:bg-white/5 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-brand-blue transition-all resize-none"
                            value={formData.description}
                            onChange={handleInputChange}
                        />
                    </div>
                </div>
            </div>

            {/* Content Section (Multiple Chapters) */}
            <div className={`p-6 rounded-2xl border shadow-sm ${surfaceClass}`}>
                <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="font-bold text-lg flex items-center gap-2">
                          <Upload size={18} className="text-brand-blue" />
                          Content
                      </h2>
                      <p className="text-xs opacity-60">Use whatever you have: memory, documents, imagination, or all three.</p>
                    </div>
                    <span className="text-xs opacity-50 font-bold uppercase tracking-wider">{chapters.length} Chapters</span>
                </div>
                
                <div className="space-y-4">
                    {chapters.map((chapter, index) => {
                        const isExpanded = expandedChapterIndex === index;
                        const wordCount = chapter.content.split(/\s+/).filter(w => w.length > 0).length;

                        return (
                            <div key={index} className={`rounded-xl border transition-all overflow-hidden ${isExpanded ? 'border-brand-blue/30 shadow-md' : 'border-black/5 dark:border-white/5'}`}>
                                {/* Chapter Header */}
                                <div 
                                    className={`p-4 flex items-center justify-between cursor-pointer ${isExpanded ? 'bg-brand-blue/5' : 'hover:bg-black/5 dark:hover:bg-white/5'}`}
                                    onClick={() => toggleChapter(index)}
                                >
                                    <div className="flex items-center gap-3">
                                        <span className="text-xs font-bold opacity-30 w-4">{index + 1}</span>
                                        <span className={`font-serif font-bold text-sm ${!chapter.title ? 'opacity-30' : ''}`}>
                                            {chapter.title || 'Untitled Chapter'}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className="text-[10px] opacity-40 font-bold uppercase tracking-wider">{wordCount} words</span>
                                        {isExpanded ? <ChevronUp size={16} className="opacity-30"/> : <ChevronDown size={16} className="opacity-30"/>}
                                    </div>
                                </div>

                                {/* Chapter Editor */}
                                {isExpanded && (
                                    <div className="p-4 pt-0 border-t border-black/5 dark:border-white/5 bg-transparent">
                                        <div className="space-y-4 mt-4">
                                            <div>
                                                <label className="block text-xs font-bold uppercase tracking-wider opacity-50 mb-1.5">Chapter Title</label>
                                                <p className="text-xs opacity-60 mb-1">Start simple. You can always change it.</p>
                                                <input 
                                                    type="text" 
                                                    placeholder={`Chapter ${index + 1}`}
                                                    className="w-full bg-black/5 dark:bg-white/5 rounded-xl px-4 py-2 outline-none focus:ring-2 focus:ring-brand-blue transition-all font-serif"
                                                    value={chapter.title}
                                                    onChange={(e) => handleChapterChange(index, 'title', e.target.value)}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold uppercase tracking-wider opacity-50 mb-1.5">Content</label>
                                                <p className="text-xs opacity-60 mb-1">Tell the story.</p>
                                                <textarea 
                                                    rows={8}
                                                    placeholder="Use whatever you have: memory, documents, imagination, or all three."
                                                    className="w-full bg-black/5 dark:bg-white/5 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-brand-blue transition-all font-serif text-base leading-relaxed opacity-80"
                                                    value={chapter.content}
                                                    onChange={(e) => handleChapterChange(index, 'content', e.target.value)}
                                                />
                                            </div>
                                            <div className="flex justify-end pt-2">
                                                <button 
                                                    type="button"
                                                    onClick={(e) => { e.stopPropagation(); removeChapter(index); }}
                                                    className="text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 px-3 py-2 rounded-lg transition-colors flex items-center gap-1.5"
                                                >
                                                    <Trash2 size={14} /> Remove Chapter
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    <Button type="button" variant="outline" fullWidth onClick={addChapter} className="border-dashed border-2 py-3 hover:bg-black/5 dark:hover:bg-white/5">
                        <Plus size={16} className="mr-2" /> Add New Chapter
                    </Button>
                </div>
            </div>

            <div className="pt-4 pb-12">
                <Button 
                    type="submit" 
                    fullWidth 
                    size="lg" 
                    disabled={isSubmitting || !formData.title || chapters.length === 0}
                    className="shadow-xl shadow-brand-blue/20"
                >
                    {isSubmitting ? 'Publishing...' : `Publish Case File (${chapters.length} Chapter${chapters.length === 1 ? '' : 's'})`}
                </Button>
                <p className="text-xs text-center mt-4 opacity-50">
                    By publishing, you confirm this case is okay to share in public and follows the platform guidelines.
                </p>
            </div>

        </form>
      </div>
    </div>
  );
};
